import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Copy, Mail, CheckCircle, XCircle, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PendingApproval() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const navigate = useNavigate();

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/user/parent-approval-status`, { withCredentials: true });
      setStatus(res.data);
      if (res.data.status === 'approved') {
        toast.success('Your parent approved — redirecting…');
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
     
  }, []);

  const verifyUrl = status?.verify_path ? `${window.location.origin}${status.verify_path}` : '';

  const copy = () => {
    navigator.clipboard.writeText(verifyUrl);
    toast.success('Link copied');
  };

  const resend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/user/resend-parent-consent`, {}, { withCredentials: true });
      toast.success('Email re-sent to your parent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend');
    } finally { setResending(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="mono-data text-sm animate-pulse-red">Loading…</div></div>;

  if (!status?.has_request) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-zinc-600 mb-4">No pending approval request.</p>
          <button onClick={() => navigate('/complete-profile')} className="btn-primary text-xs px-6 py-2">Complete Profile</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12" data-testid="pending-approval-page">
      <div className="container-swiss max-w-lg">
        <div className="border border-[#09090B] p-8">
          {status.status === 'pending' && (
            <>
              <Clock className="w-10 h-10 text-[#52525b] mb-4 animate-pulse" />
              <h1 className="text-2xl font-black tracking-tighter mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Waiting for Parent Approval
              </h1>
              <p className="text-sm text-zinc-600 mb-2" data-testid="pending-status">
                We sent a consent request to <strong>{status.parent_email}</strong>.
              </p>
              <p className="text-xs text-zinc-500 mb-6">
                Your account activates the moment your parent approves. This page refreshes automatically.
              </p>

              <div className="border border-[#09090B] bg-zinc-100 p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#09090B]" />
                    <span className="mono-data text-xs text-[#09090B]">EMAIL NOT ARRIVED?</span>
                  </div>
                  <button onClick={resend} disabled={resending}
                    className="text-[10px] mono-data text-[#09090B] hover:underline disabled:opacity-40 flex items-center gap-1"
                    data-testid="resend-email-btn">
                    <Send className="w-3 h-3" /> {resending ? 'Sending…' : 'Resend'}
                  </button>
                </div>
                <p className="text-xs text-zinc-700 mb-3">
                  Share this direct link with your parent (WhatsApp, SMS, or in person):
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[10px] bg-white border border-zinc-300 p-2 break-all" data-testid="verify-url">{verifyUrl}</code>
                  <button onClick={copy} className="btn-secondary text-xs px-3 py-2 flex items-center gap-1" data-testid="copy-link-btn">
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
              </div>
            </>
          )}

          {status.status === 'approved' && (
            <>
              <CheckCircle className="w-10 h-10 text-[#09090B] mb-4" />
              <h1 className="text-2xl font-black tracking-tighter mb-2">Approved!</h1>
              <p className="text-sm text-zinc-600">Redirecting to your dashboard…</p>
            </>
          )}

          {status.status === 'denied' && (
            <>
              <XCircle className="w-10 h-10 text-[#09090B] mb-4" />
              <h1 className="text-2xl font-black tracking-tighter mb-2">Consent Denied</h1>
              <p className="text-sm text-zinc-600">
                Your parent declined the consent request. Contact them to discuss, or email{' '}
                <a href="mailto:privacy@storytellercharles.com" className="text-[#09090B] underline">privacy@storytellercharles.com</a>.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
